import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';
import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import { ReleaseEnvironment } from 'types/releaseEnvironment';
import * as releaseApi from 'api/releaseApi';
import {
  listSelectors,
  listActions,
} from 'store/reducers/releaseReducers/releaseEnvironmentReducer/listSlice';
import { editActions } from 'store/reducers/releaseReducers/releaseEnvironmentReducer/editSlice';

/**
 * リリース環境一覧 store の実装を環境から隠すための中間層
 * @returns selector と action のラッパー
 */
export const useListStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(listSelectors.loading);
  const releaseEnvironments = useAppSelector(listSelectors.releaseEnvironments);
  const fetchReleaseEnvironments = useCallback(async () => {
    dispatch(listActions.loadStarted());
    const environmentResult = await releaseApi.fetchReleaseEnvironments();
    const componentResult = await releaseApi.fetchReleaseComponents();

    if (!environmentResult.ok) {
      toast({
        status: 'error',
        description: `環境一覧の取得に失敗しました: ${environmentResult.err.message}`,
      });
    } else if (!componentResult.ok) {
      toast({
        status: 'error',
        description: `環境一覧の取得に失敗しました: ${componentResult.err.message}`,
      });
    } else {
      dispatch(
        listActions.fetchReleaseEnvironmentSuccess({
          releaseEnvironments: environmentResult.value,
        }),
      );
      dispatch(
        listActions.fetchReleaseComponentSuccess({
          releaseComponents: componentResult.value,
        }),
      );
    }
    dispatch(listActions.loadEnded());
  }, [dispatch, toast]);

  const componentList = useAppSelector(listSelectors.releaseComponents).map(
    (value) => ({
      id: value.id,
      componentName: value.componentName,
    }),
  );

  const onOpenModal = useCallback(
    (releaseEnvironment: ReleaseEnvironment) => {
      dispatch(editActions.modalOpened(releaseEnvironment));
    },
    [dispatch],
  );

  return {
    /** 一覧ロード状態 */
    loading,
    /** リリース環境一覧データ */
    releaseEnvironments,
    /** リリース環境一覧を取得 */
    fetchReleaseEnvironments,
    /** 詳細を表示 */
    onOpenModal,
    componentList,
  };
};
